import { Link } from 'react-router-dom'
import { formatCountBrowser, useCommonT } from '../../i18n'
import type { SavedDeck } from '../../hooks/useSavedDecks'
import type { CardImage } from '../../api/cardImages'
import RecentDeckTile from './RecentDeckTile'
import { CardStackIcon } from '../shared/TileIcons'
import { BuildIcon, ExploreIcon, JudgeIcon } from '../layout/navIcons'
import { deckPath } from '../search/search.codes'
import { isAndroidApp } from '../../lib/androidApp'

// The phone Home of navigation v2 (plan nav-search-2026-10-16, Target "Home:
// collection card, tiles Build / Ask the Judge / Explore, recent decks";
// canvas ABHome). Phones only (md:hidden): desktop Home is unchanged. The
// parent renders it only with the nav v2 flag on, and passes what Home
// already loads (no new request): the stored unique-name count and the
// recent decks from Home's own saved-deck list with the art Home's one
// batched lookup already resolved (`getImage`). The collection's dollar
// value needs an API read per Home view, so the card shows the count only.
//
// The recent decks are the same art tiles (RecentDeckTile) as the desktop
// rail, 2-up, every deck Home passes (up to 4). A plain text list shipped
// first and read as a regression on device (2026-10-18).

const TILES = [
  { path: '/build', id: 'build', Icon: BuildIcon },
  { path: '/rules', id: 'judge', Icon: JudgeIcon },
  { path: '/explore', id: 'explore', Icon: ExploreIcon },
] as const

export default function PhoneHomeV2({ hasCollection, cardCount, recentDecks, getImage }: {
  hasCollection: boolean
  cardCount: number | null
  recentDecks: readonly SavedDeck[]
  getImage?: (name: string) => CardImage | undefined
}) {
  const t = useCommonT()
  const recent = recentDecks
  return (
    <div data-testid="phone-home-v2" className="space-y-4 text-left md:hidden">
      <Link
        to="/collection"
        className="surface block p-4 transition-colors hover:border-gray-700"
      >
        {hasCollection ? (
          <>
            <span className="block text-xs font-semibold uppercase tracking-wide text-gray-400">{t('home.v2.collection_label')}</span>
            <span className="mt-1 block text-2xl font-semibold text-white">
              {cardCount != null
                ? t('home.collection.count_unique', { n: formatCountBrowser(cardCount) })
                : t('home.v2.collection_open')}
            </span>
          </>
        ) : (
          <span className="flex items-center gap-3">
            <CardStackIcon className="w-8 h-8 shrink-0 text-purple-400" />
            <span>
              <span className="block text-base font-semibold text-white">{t('home.v2.start_collection')}</span>
              {/* Inside our Android app the scanner is the app's own Scan button. */}
              <span className="mt-0.5 block text-sm text-gray-400">
                {t(isAndroidApp() ? 'home.v2.start_collection_body_android' : 'home.v2.start_collection_body')}
              </span>
            </span>
          </span>
        )}
      </Link>

      {/* Grid rows stretch, so the three tiles share one height; each centers
          its icon over a label of at most two centered lines. */}
      <div className="grid grid-cols-3 gap-2.5">
        {TILES.map(({ path, id, Icon }) => (
          <Link
            key={path}
            to={path}
            data-testid={`phone-home-v2-tile-${id}`}
            className="surface flex min-h-[5.5rem] flex-col items-center justify-center gap-2 px-2 py-3 text-center transition-colors hover:border-gray-700"
          >
            <Icon className="w-6 h-6 shrink-0 text-purple-400" />
            <span className="line-clamp-2 text-balance text-sm font-semibold leading-tight text-white">{t(`home.v2.tile_${id}`)}</span>
          </Link>
        ))}
      </div>

      {recent.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400">{t('home.recent')}</h2>
          <div className="grid grid-cols-2 gap-3">
            {recent.map(deck => (
              <RecentDeckTile
                key={deck.id}
                deck={deck}
                image={deck.commander ? getImage?.(deck.commander) : undefined}
                partnerImage={deck.partner ? getImage?.(deck.partner) : undefined}
                to={deckPath(deck.id)}
                caption={t('search.deck_bracket', { n: deck.bracket })}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
